import useAuthUser from "hooks/auth-user";
import Appointment, { needsMissedAppointmentPayment } from "models/appointment";
import { getLastAppointment } from "models/user";
import React, {
    createContext,
    ReactNode,
    useState,
    useContext,
    useEffect,
    useCallback,
} from "react";

type LastAppointmentContextType = {
    hasToPayLastMissedAppointment: boolean;
    lastAppointment: Appointment | undefined;
    appointmentToPay: Appointment | undefined;
    showPaymentForm: boolean;
    openPaymentForm: (appointment: Appointment) => void;
    closePaymentForm: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const LastAppointmentContext = createContext<LastAppointmentContextType>(null!);

export const LastAppointmentProvider = (props: {
    children: ReactNode;
}): React.ReactElement => {
    const [authUser] = useAuthUser();
    const [appointmentToPay, setAppointmentToPay] = useState<
        Appointment | undefined
    >(undefined);
    const [lastAppointment, setLastAppointment] = useState<
        Appointment | undefined
    >(undefined);
    const [
        hasToPayLastMissedAppointment,
        setHasToPayLastMissedAppointment,
    ] = useState<boolean>(false);

    const openPaymentForm = useCallback((appointment: Appointment) => {
        setAppointmentToPay(appointment);
    }, []);

    const closePaymentForm = useCallback(() => {
        setAppointmentToPay(undefined);
    }, []);

    useEffect(() => {
        if (authUser) {
            setLastAppointment(getLastAppointment(authUser));

            if (lastAppointment) {
                setHasToPayLastMissedAppointment(
                    needsMissedAppointmentPayment(lastAppointment)
                );
            }
        }
    }, [
        authUser,
        lastAppointment?.missedAppointmentPaymentStripeId,
        lastAppointment?.shouldRetake,
        lastAppointment?.canRescheduleFreely,
    ]);

    const contextValue = {
        hasToPayLastMissedAppointment,
        lastAppointment,
        appointmentToPay,
        showPaymentForm: !!appointmentToPay,
        openPaymentForm,
        closePaymentForm,
    };

    return (
        <LastAppointmentContext.Provider value={contextValue}>
            {props.children}
        </LastAppointmentContext.Provider>
    );
};

export function useLastAppointment(): LastAppointmentContextType {
    return useContext(LastAppointmentContext);
}
